import { useEffect, useState } from 'react';
import { listUsers, deleteUser } from '../backend/services/userAdmin.ts';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Link } from 'react-router';

const BRAND_GREEN = '#1D9E75';

const bodyCellSx = {
  fontFamily:   "'DM Mono', monospace",
  fontSize:     13,
  color:        'var(--color-text-primary)',
  borderBottom: '1px solid rgba(255,255,255,0.04)',
  background:   'transparent',
  py:           2,
};

export default function UsersPage() {
  const [users, setUsers]           = useState<any[]>([]);
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);

  const loadUsers = async () => {
    try {
      const res = await listUsers(search);
      setUsers(res);
    } catch (err: any) {
      console.error('Failed to load users:', err);
    }
  };

  useEffect(() => { loadUsers(); }, [search]);

  const handleDelete = async () => {
    if (!deleteUserId) return;
    try {
      const res = await deleteUser(deleteUserId);
      if (!res) throw new Error('Failed to delete user');
      setDeleteUserId(null);
      loadUsers();
    } catch (err: any) {
      console.error('Failed to delete user:', err.message);
    }
  };

  const paginated = users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: '0 1.5rem 2.5rem', width: '100%', height: '100vh', boxSizing: 'border-box' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography sx={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 20,
          fontWeight: 600,
          color: BRAND_GREEN,
        }}>
          User Management
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TextField
            placeholder="Search users"
            size="small"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: "'DM Mono', monospace",
                fontSize: 12,
                borderRadius: 2,
                '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                '&:hover fieldset': { borderColor: BRAND_GREEN },
                '&.Mui-focused fieldset': { borderColor: BRAND_GREEN },
              },
            }}
          />
          <Button
            variant="outlined"
            size="small"
            component={Link}
            to="/users/create"
            startIcon={<PersonAddIcon sx={{ fontSize: '14px !important', padding: '0 !important' }} />}
            sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              borderColor: BRAND_GREEN,
              color: BRAND_GREEN,
              borderRadius: 2,
              px: 1.5,
              '&:hover': {
                borderColor: BRAND_GREEN,
                background: 'rgba(29,158,117,0.08)',
              },
            }}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <Box sx={{ background: 'var(--color-bg-secondary)', height: '85%', borderRadius: 3, p: '0.5rem 1.25rem 0', mt: 3 }}>
        <TableContainer sx={{ background: 'transparent', height: '100%' }}>
          <Table aria-label="users table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 2 }}>Username</TableCell>
                <TableCell sx={{ py: 2 }}>Email</TableCell>
                <TableCell sx={{ py: 2 }}>Role</TableCell>
                <TableCell sx={{ py: 2, width: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map(user => (
                <TableRow key={user.id} sx={{ '&:last-child td': { border: 0 } }}>
                  <TableCell sx={{ ...bodyCellSx, fontFamily: "'Syne', sans-serif", fontWeight: 500 }}>
                    {user.username}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, color: 'var(--color-text-secondary)' }}>
                    {user.email}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {user.groups}
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        component={Link}
                        to={`/users/edit/${user.id}`}
                        size="small"
                        sx={{
                          color: BRAND_GREEN,
                          '& svg': { fontSize: '16px !important', padding: '0 !important' },
                          '&:hover': { background: 'rgba(29,158,117,0.1)' },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteUserId(user.id)}
                        sx={{
                          color: '#993C1D',
                          '& svg': { fontSize: '16px !important', padding: '0 !important' },
                          '&:hover': { background: 'rgba(153,60,29,0.1)' },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={users.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={e => setRowsPerPage(parseInt(e.target.value, 10))}
          sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 12,
            color: 'var(--color-text-secondary)',
            borderTop: '1px solid rgba(255,255,255,0.04)',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
            },
          }}
        />
      </Box>

      {/* Delete dialog */}
      <Dialog
        open={!!deleteUserId}
        onClose={() => setDeleteUserId(null)}
        PaperProps={{
          sx: {
            background:   'var(--color-bg-secondary)',
            borderRadius: 3,
            p:            1,
            minWidth:     320,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 600, color: 'var(--color-text-primary)' }}>
          Delete User
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: 'var(--color-text-secondary)' }}>
            Are you sure you want to delete this user? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteUserId(null)}
            sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              textTransform: 'uppercase',
              color: 'var(--color-text-secondary)',
              borderRadius: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              textTransform: 'uppercase',
              background: '#993C1D',
              borderRadius: 2,
              '&:hover': { background: '#7a2e15' },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
